"use client"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonLink from "@/app/(site)/_components/Molecules/ButtonLink"
import { useClickOutside } from "@/app/(site)/_utils/_hooks/useOutsideIdentifier"
import { getLangCode } from "@/app/(site)/_utils/misc"
import useGlobalStore from "@/app/(site)/contexts/ecommerce/store"
import {
    setAddedProduct,
    useAppDispatch,
    useAppSelector
} from "@/app/(site)/contexts/Global"
import { t } from "@/translations"
import Image from "next/image"
import React, { useCallback } from "react"

import { ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import placeholder from "@assets/images/placeholder.png"
import { usePathname } from "next/navigation"

type AddToCartModalProps = {
    product?: ProductData
    isCart?: boolean
}

export default function AddToCartModal({ product, isCart }: AddToCartModalProps) {
    const [isAnimating, setIsAnimating] = React.useState(false)
    const modalRef = React.useRef<HTMLDivElement>(null)
    const dispatch = useAppDispatch()
    const addedProduct = useAppSelector(state => state.product.addedProduct) // Access added product
    // Getting langCode
    const langCode = getLangCode(usePathname().split("/")[1]) || ""

    // Zustand
    const isCartMenuOpen = useGlobalStore(state => state.isCartMenuOpen)
    const toggleCartMenu = useGlobalStore(state => state.toggleCartMenu)
    const closeCartMenu = useGlobalStore(state => state.closeCartMenu)

    // Redux
    const state = useAppSelector(state => state.product)

    const selectedSizeName = addedProduct?.size || state.selectedSize
    const autoCloseDuration = 2000

    useClickOutside({ refs: [modalRef], onOutsideClick: toggleCartMenu })

    const handleModuleClose = useCallback(() => {
        setIsAnimating(false)
        setTimeout(() => {
            toggleCartMenu()
            dispatch(
                setAddedProduct({
                    name: "",
                    size: "",
                    price: "",
                    image: "",
                    color_name: ""
                })
            )
        }, 300)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleCartMenu])

    React.useEffect(() => {
        let timer: NodeJS.Timeout
        if (isCartMenuOpen) {
            setIsAnimating(true)

            // Set a timer to automatically close the modal
            timer = setTimeout(() => {
                handleModuleClose()
            }, autoCloseDuration)
        } else {
            setIsAnimating(false)
        }

        // Clear the timer if the component unmounts or modal closes early
        return () => clearTimeout(timer)
    }, [handleModuleClose, isCartMenuOpen, addedProduct])

    if (!isCartMenuOpen) return null

    const moduleClasses = isAnimating
        ? "opacity-100 translate-y-0"
        : "opacity-0 translate-x-[50px]"

    const imageSrc = addedProduct?.image ?? product?.media.small?.[0]?.url
    return (
        <div>
            <div
                className={`fixed top-0 z-10 h-full w-[100%] bg-black/20 backdrop-blur-md transition-all duration-300 ease-in-out ${moduleClasses}`}
                onClick={() => handleModuleClose()}
                aria-hidden="true"
            />

            <div
                ref={modalRef}
                className={`fixed bottom-0 left-0 right-0 z-40 m-24 grid grid-cols-6 gap-16 bg-main-white p-24 sm:left-auto sm:w-390 md:bottom-auto md:right-24 md:top-80 ${moduleClasses}`}
                role="dialog"
                aria-modal="true"
            >
                <Text size="medium" weight="regular" className="col-span-5">
                    {t(langCode, "Product added to cart")}
                </Text>
                <div
                    className="col-span-1 flex cursor-pointer justify-end"
                    onClick={() => toggleCartMenu()}
                    role="button"
                    aria-label="Toggle add to cart modal"
                >
                    <IconClose />
                </div>
                <div className="col-span-2">
                    {imageSrc && (
                        <Image
                            src={imageSrc || placeholder}
                            alt={addedProduct?.name ?? product?.name ?? ""}
                            width={500}
                            height={500}
                        />
                    )}
                </div>
                <div className="col-span-4">
                    <div className="flex flex-col justify-between">
                        <div>
                            <Text size="medium" weight="regular">
                                {addedProduct?.name ?? product?.name}
                            </Text>
                            <Text
                                size="medium"
                                weight="regular"
                                className="text-main-dark-grey"
                            >
                                {addedProduct?.color_name ??
                                    product?.color_swatch?.color_name}
                            </Text>
                            <Text
                                size="medium"
                                weight="regular"
                                className="text-main-dark-grey"
                            >
                                {state?.availableSizes?.length > 1 ? (
                                    selectedSizeName
                                ) : (
                                    <>{t(langCode, "One size")}</>
                                )}
                            </Text>
                        </div>
                        <Text size="medium" weight="regular">
                            {addedProduct?.price ?? product?.price}
                        </Text>
                    </div>
                </div>
                {!isCart && (
                    <div className="col-span-6 mt-140">
                        <ButtonLink
                            href="/cart"
                            onClick={closeCartMenu}
                            color="black"
                            size="large"
                            variant="primary"
                        >
                            {t(langCode, "Go to cart")}
                        </ButtonLink>
                    </div>
                )}
            </div>
        </div>
    )
}
